import { gql, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
import { isEqual } from 'lodash';
import React, { useMemo, useState } from 'react';

import {
  ClientFilterView,
  SelectedClients,
} from 'src/components/clientFilter/ClientFilter';
import { ClientFilterEmpty } from 'src/components/clientFilter/clientFilterEmpty/ClientFilterEmpty';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { TimeSecondsAgo } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  ExcludedClient,
  excludedClientsToSelectedClients,
  selectedClientsToExcludedClients,
} from '../transforms';

export const listAllClientsQuery = gql`
  query ListAllClientsQuery(
    $graphId: ID!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
  ) {
    service(id: $graphId) {
      id
      stats: statsWindow(from: $timeFrom, to: $timeTo) {
        operationCheckStats {
          group: groupBy {
            clientName
          }
        }
      }
    }
  }
`;

export const listClientVersionsQuery = gql`
  query ListClientVersionsQuery(
    $graphId: ID!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $clientNames: [String]
  ) {
    service(id: $graphId) {
      id
      versionStats: statsWindow(from: $timeFrom, to: $timeTo) {
        operationCheckStats(filter: { in: { clientName: $clientNames } }) {
          group: groupBy {
            clientName
            clientVersion
          }
        }
      }
    }
  }
`;

interface Props {
  isOpen: boolean;
  graphId: string;
  showClientsFromTime: TimeSecondsAgo;
  excludedClients: ExcludedClient[] | null;
  onUpdate: (excludedClients: GraphQLTypes.ClientFilterInput[] | null) => void;
  onClose: () => void;
}

function mergeRawClient(
  clientsGroupedByName: Record<string, Array<string>>,
  client: { name: string | null; version: string | 'loading' | null },
) {
  // We choose the empty string as the representation of a null
  // value, since it can be stringified and included as a key as
  // well as a value. Since protobuf and our reporting treat the
  // empty string and not including the value as the same, we do not
  // lose any information.
  const name = client.name ?? '';
  const version = client.version ?? '';
  const versions = clientsGroupedByName[name] ?? [];
  if (!versions.includes(version)) {
    versions.push(version);
  }
  if (versions.length > 1 && versions.includes('loading')) {
    versions.splice(versions.indexOf('loading'), 1);
  }
  return {
    ...clientsGroupedByName,
    [name]: versions,
  };
}

/**
 * Merges excluded clients list with clients from stats.
 */
function buildAllClientsGroupedByName(
  excludedClients: Props['excludedClients'],
  queryStats?: GraphQLTypes.ListAllClientsQuery_service_stats_operationCheckStats[],
  versionStats?: GraphQLTypes.ListClientVersionsQuery_service_versionStats_operationCheckStats[],
) {
  const allClients = queryStats
    ? queryStats.reduce<Record<string, Array<string>>>(
        (groupedClients, { group: { clientName } }) =>
          mergeRawClient(groupedClients, {
            name: clientName,
            version: 'loading',
          }),
        {},
      )
    : {};

  const clientsWithVersions = versionStats
    ? versionStats.reduce<Record<string, Array<string>>>(
        (groupedClients, { group: { clientName, clientVersion } }) =>
          mergeRawClient(groupedClients, {
            name: clientName,
            version: clientVersion,
          }),
        allClients,
      )
    : {};

  return excludedClients
    ? excludedClients.reduce(
        (groupedClients, client) =>
          // don't include null client names
          client.name ? mergeRawClient(groupedClients, client) : groupedClients,
        clientsWithVersions,
      )
    : clientsWithVersions;
}

export const ConfigureExcludedClientsModal = ({
  isOpen,
  graphId,
  showClientsFromTime,
  excludedClients,
  onUpdate,
  onClose,
}: Props) => {
  const [selectedClients, setSelectedClients] =
    useState<SelectedClients | null>(
      excludedClientsToSelectedClients(excludedClients),
    );
  const [expandedClients, setExpandedClients] = useState<Set<string> | null>();

  const { data, loading, error } = useQuery<
    GraphQLTypes.ListAllClientsQuery,
    GraphQLTypes.ListAllClientsQueryVariables
  >(listAllClientsQuery, {
    variables: {
      graphId,
      timeFrom: showClientsFromTime,
      timeTo: '-0',
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    accountId: currentAccountId as string,
    skip: !currentAccountId,
  });

  const { previousData: previousVersionData, data: versionData } = useQuery<
    GraphQLTypes.ListClientVersionsQuery,
    GraphQLTypes.ListClientVersionsQueryVariables
  >(listClientVersionsQuery, {
    variables: {
      graphId,
      timeFrom: showClientsFromTime,
      timeTo: '-0',
      clientNames: Array.from(expandedClients ?? []),
    },
    skip: expandedClients?.size === 0,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const allClients = useMemo(
    () =>
      buildAllClientsGroupedByName(
        excludedClients,
        data?.service?.stats?.operationCheckStats,
        versionData
          ? versionData.service?.versionStats?.operationCheckStats
          : previousVersionData?.service?.versionStats?.operationCheckStats,
      ),
    [data, versionData, previousVersionData, excludedClients],
  );

  const selectionDirty = useMemo(
    () =>
      !isEqual(
        excludedClients,
        selectedClientsToExcludedClients(selectedClients),
      ),
    [excludedClients, selectedClients],
  );

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Add clients to exclusion list</ModalTitle>
          <ModalDescription>
            Any operations sent by these clients will not be checked against
            changes to the schema.
          </ModalDescription>
        </ModalHeader>
        <ModalBody className="pt-0">
          {loading || currentPlan.loading ? (
            <div className="flex items-center justify-center">
              <Spinner />
            </div>
          ) : error || currentPlan.error ? (
            <ErrorMessage />
          ) : Object.keys(allClients).length === 0 ? (
            <ClientFilterEmpty />
          ) : (
            <ClientFilterView
              clientThresholdToShowSearch={10}
              canViewVersions={currentPlan.currentPlan?.clientVersions ?? false}
              allClients={allClients}
              preserveClientNames={true}
              showAnyVersionOption={true}
              // TODO(tim): change the typings to use null here instead of undefined
              selectedClients={selectedClients ?? undefined}
              setSelectedClients={setSelectedClients}
              onClientCollapsed={(clientName, collapsed) => {
                if (!collapsed) {
                  setExpandedClients(
                    (prevExpandedClients) =>
                      new Set([...(prevExpandedClients ?? []), clientName]),
                  );
                }
              }}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              isDisabled={selectedClients === null || !selectionDirty}
              onClick={() =>
                onUpdate(selectedClientsToExcludedClients(selectedClients))
              }
            >
              Update Exclusions
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
